<template>
    <transition name="fade">
        <div
            v-if="toast.opened"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            class="fixed bottom-24 left-0 right-0 z-99999 mx-auto flex max-w-md items-center justify-between gap-3 rounded border border-theme-border bg-theme-button-bg px-4 text-theme-text opacity-95 shadow"
            :class="toast.progress ? 'pb-6 pt-3' : 'py-3'"
        >
            <svg-icon
                v-if="toast.icon"
                :icon="toast.icon"
                class="shrink-0 text-theme-icon"
                width="24px"
                height="24px"
            />
            <p :id="textId" class="m-0 flex-1 text-left">{{ toast.text }}</p>
            <!-- A link action is a native link, so the user's own tap opens the tab:
                 routed through the host it would become a window.open() the host makes a
                 bridge round trip later, which iOS does not treat as the user's. -->
            <BaseButton
                v-if="toast.action"
                v-bind="actionLinkAttributes"
                :href="toast.action.href"
                variant="primary"
                size="sm"
                class="shrink-0"
                @click="runAction"
            >
                {{ toast.action.label }}
            </BaseButton>
            <PillIconButton
                class="shrink-0"
                icon="close"
                variant="muted"
                :icon-size="14"
                :aria-label="localizations[lang].tooltips.close"
                @click="closeToast"
            />
            <div
                v-if="toast.progress"
                role="progressbar"
                :aria-labelledby="textId"
                class="absolute bottom-3 left-4 right-4 h-1 overflow-hidden rounded-full bg-theme-progress-track"
            >
                <!-- Held still, a third of the track would read as a third of the work done. -->
                <div
                    class="h-full w-1/3 animate-toast-progress rounded-full bg-theme-accent motion-reduce:w-full motion-reduce:animate-none"
                />
            </div>
        </div>
    </transition>
</template>
<script>
import { mapMutations, mapState } from 'vuex';
import { DEFAULT_TOAST_DURATION_MS } from '_shared_/js/constants/toastDurations';
import BaseButton from '_shared_/UI/Components/buttons/BaseButton.vue';
import PillIconButton from '_shared_/UI/Components/PillIconButton.vue';

export default {
    name: 'ComponentToast',
    components: { BaseButton, PillIconButton },
    data() {
        return {
            textId: `toast-text-${this._uid}`,
        };
    },
    computed: {
        ...mapState(['toast', 'localizations', 'lang']),
        actionLinkAttributes() {
            return this.toast.action?.href
                ? { target: '_blank', rel: 'noopener noreferrer', 'data-native-link': '' }
                : {};
        },
    },
    watch: {
        'toast.text'() {
            this.restartCloseTimerIfOpened();
        },
        // A persistent toast can replace one with the same text, which the text watcher never sees.
        'toast.persistent'() {
            this.restartCloseTimerIfOpened();
        },
    },
    mounted() {
        this.initCloseToast();
    },
    beforeDestroy() {
        this.clearCloseToastTimer();
    },
    methods: {
        ...mapMutations(['openToast', 'closeToast']),
        clearCloseToastTimer() {
            if (this.closeToastTimer) {
                clearTimeout(this.closeToastTimer);
                this.closeToastTimer = null;
            }
        },
        restartCloseTimerIfOpened() {
            if (this.toast.opened) {
                this.initCloseToast();
            }
        },
        initCloseToast() {
            this.clearCloseToastTimer();
            if (this.toast.persistent) {
                return;
            }
            this.closeToastTimer = setTimeout(() => {
                this.closeToast();
            }, this.toast.durationMs ?? DEFAULT_TOAST_DURATION_MS);
        },
        /**
         * Closes the toast before dispatching, so a toast the action opens is not
         * closed along with this one. A link action only closes it, and the
         * browser follows the link.
         */
        runAction() {
            const { dispatch, payload } = this.toast.action;
            this.closeToast();
            if (dispatch) {
                this.$store.dispatch(dispatch, payload);
            }
        },
    },
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity duration-500;
}
.fade-enter,
.fade-leave-to {
    @apply opacity-0;
}
</style>
